import _                                             from 'underscore';
import React, { Context, FC, ReactNode, useContext } from 'react';
import { Route, Redirect, useLocation }              from 'react-router-dom';

import { PermissionBasedFeatureFlagKeys, useCurrentUser, useFeatureFlags } from '../../../../hooks';
import { RoleAccessService, RolePolicyContext, RolePolicyType, RoleType }  from '../../../../services/RoleAccessService';
import { ORGANIZATIONAL_ROLES, PROJECT_ROLES, SYSTEM_ROLES }               from '../../../../constants/ProductEnums';
import { Uuid }                                                            from '../../../../types';
import { ProjectContextType }                                              from '../../../pages/projects/ProjectIndexPage';
import { UserContextType }                                                 from '../../../pages/users/UserIndexPage';

export interface RoleAccessControlProps {

    /* A list of policies that should be satisfied by the context user's role access */
    policies?: RolePolicyType[],

    /* A list of roels that should be satisfied by the context user's role type */
    roles?: RoleType[],

    /* Needed to specify what role type to inspect on */
    roleContext?: RolePolicyContext,

    /* Needed when accessing organization type access (RolePolicyContext = 'ORGANIZATION') */
    organizationId?: Uuid,

    /* Needed when accessing project type access (RolePolicyContext = 'PROJECT') */
    projectId?: Uuid,

    /* Should access be granted only when user has all criteria accepted? */
    inclusiveCriteria?: boolean,

    /* Should access be granted only when user has all access to all policies? */
    inclusivePolicies?: boolean,

    /* Should access be granted only when any user role has all access to all policies */
    inclusiveRoles?: boolean,

    /* What to replace with if no access */
    replaceWith?: ReactNode,

    /* Redirect route path */
    redirect?: string,

    /* Bypass prop to allow if external criteria is met */
    featureFlag?: ( () => boolean ) | PermissionBasedFeatureFlagKeys | string[],

    /* Bypass prop value to allow if flag criteria is the value specified */
    featureFlagPassValue?: any,

    /* Bypass prop to allow if external criteria is met */
    allowIf?: ( () => boolean ) | boolean,

    /* What happens when we have denied the access */
    onDeny?: () => void,

    /* A context object to help specify project and organization for us. This must be a context object with { organizationId, projectId } */
    context?: ProjectContextType | UserContextType,
}

const defaultProps: RoleAccessControlProps = {
    policies:             [],
    roles:                [],
    roleContext:          null,
    organizationId:       null,
    projectId:            null,
    inclusiveCriteria:    false,
    inclusivePolicies:    true,
    inclusiveRoles:       false,
    replaceWith:          null,
    redirect:             null,
    allowIf:              null,
    onDeny:               _.noop,
    context:              null,
    featureFlagPassValue: true,
}

const RoleAccessContext = React.createContext<ProjectContextType | UserContextType>( {
    projectId:      null,
    organizationId: null,
} );

const RoleAccessControl: FC<RoleAccessControlProps> = ( props ) => {

    let location     = useLocation(),
        featureFlags = useFeatureFlags();

    let {
            currentUser,
            hasSystemRoleAccessTo,
            hasOrganizationalRoleAccessTo,
            hasProjectRoleAccessTo,
            hasSystemRole,
            hasOrganizationalRole,
            hasProjectRole,
        } = useCurrentUser();

    // setup a context object
    let hasSpecifiedContext         = !_.isNull( props.context ),
        hasSpecifiedRoleContext     = !_.isNull( props.roleContext ),
        hasSpecifiedFlagCheck       = !_.isNull( props.featureFlag );
    let Context: ProjectContextType = useContext( hasSpecifiedContext ? props.context : RoleAccessContext as any );

    // bail if we dont have a user
    if( !( currentUser && ( hasSpecifiedRoleContext || hasSpecifiedFlagCheck ) ) ) return null;

    // get project id from specific props or context
    const getProjectId = () => {
        return hasSpecifiedContext ? Context.projectId : props.projectId;
    };

    // get org id from specific props or context
    const getOrganizationId = () => {
        return hasSpecifiedContext ? Context.organizationId : props.organizationId;
    };

    // determine if we are asking at system level
    const isSystemContext = () => {
        return ( props.roleContext === RoleAccessService.ROLE_CONTEXTS.SYSTEM );
    };

    // determine if we are asking at system level
    const isOrganizationContext = () => {
        return ( props.roleContext === RoleAccessService.ROLE_CONTEXTS.ORGANIZATION );
    };

    // determine if we are asking at system level
    const isProjectContext = () => {
        return ( props.roleContext === RoleAccessService.ROLE_CONTEXTS.PROJECT );
    };

    const hasAccessFromGivenPolicies = () => {

        let { policies, inclusivePolicies } = props,
            { roleContext }                 = props,
            organizationId                  = getOrganizationId(),
            projectId                       = getProjectId(),
            systemContext                   = isSystemContext(),
            policyAccessMap                 = [];

        policies = _.flatten( [ policies ] );

        let hasRoleContext = _.contains( _.values( RoleAccessService.ROLE_CONTEXTS ), roleContext ),
            hasPolicies    = !_.isEmpty( policies ),
            hasOrgIds      = !_.isNull( organizationId ),
            hasProjectIds  = hasOrgIds && !_.isNull( projectId );

        if( !hasPolicies ||
                !hasRoleContext || (
                        !systemContext && (
                                ( isOrganizationContext() && !hasOrgIds ) ||
                                ( isProjectContext() && !hasProjectIds )
                        )
                ) ){
            return false;
        }

        // determine policies value(s) array
        // ['ADMIN_ACCESS','CAN_ADD_ORG'] ==> [ true, false ]
        switch( roleContext ){
            case RoleAccessService.ROLE_CONTEXTS.SYSTEM:
                policyAccessMap = _.map( policies, policy => hasSystemRoleAccessTo( policy ) );
                break;
            case RoleAccessService.ROLE_CONTEXTS.ORGANIZATION:
                policyAccessMap = _.map( policies, _.partial( hasOrganizationalRoleAccessTo, organizationId ) );
                break;
            case RoleAccessService.ROLE_CONTEXTS.PROJECT:
                policyAccessMap = _.map( policies, _.partial( hasProjectRoleAccessTo, projectId ) );
                break;
        }

        return ( inclusivePolicies ) ?
               !_.contains( policyAccessMap, false ) : // inclusive (AND)
               _.contains( policyAccessMap, true );	 // exclusive (OR);
    };

    const hasAccessFromGivenRoles = () => {

        let { roles, inclusiveRoles } = props,
            { roleContext }           = props,
            organizationId            = getOrganizationId(),
            projectId                 = getProjectId(),
            systemContext             = isSystemContext(),
            roleAccessMap             = [];

        roles = _.flatten( [ roles ] );

        let hasRoleContext = _.contains( _.values( RoleAccessService.ROLE_CONTEXTS ), roleContext ),
            hasRoles       = !_.isEmpty( roles ),
            hasIds         = !_.isNull( organizationId ) && !_.isNull( projectId );

        if( !hasRoleContext || ( !systemContext && ( !hasRoles || !hasIds ) ) ){
            return false;
        }

        // determine policies value(s) array
        // ['ADMIN_ACCESS','CAN_ADD_ORG'] ==> [ true, false ]
        switch( roleContext ){
            case RoleAccessService.ROLE_CONTEXTS.SYSTEM:
                roleAccessMap = _.map( roles, hasSystemRole );
                break;
            case RoleAccessService.ROLE_CONTEXTS.ORGANIZATION:
                roleAccessMap = _.map( roles, _.partial( hasOrganizationalRole, organizationId ) );
                break;
            case RoleAccessService.ROLE_CONTEXTS.PROJECT:
                roleAccessMap = _.map( roles, _.partial( hasProjectRole, projectId ) );
                break;
        }

        return ( inclusiveRoles ) ?
               !_.contains( roleAccessMap, false ) : // inclusive (AND)
               _.contains( roleAccessMap, true );	 // exclusive (OR);
    };

    const hasAccesFromBypassCriteria = () => {

        let { allowIf } = props,
            hasAccess   = false;

        // determine bypassCriteria if available
        if( allowIf ){
            hasAccess = ( _.isFunction( allowIf ) ) ? allowIf() : allowIf;
        }

        return hasAccess;
    };

    const hasAccessFromFeatureFlag = () => {

        let { featureFlag, featureFlagPassValue, inclusiveCriteria } = props,
            hasAccess                                                = false;

        // determine featureFlag access if available
        if( featureFlag ){

            if( _.isFunction( featureFlag ) ){

                // evaluate the feature flag function given to us
                hasAccess = featureFlag();

            } else {

                // create an array of feature flag keys
                const keys        = _.isArray( featureFlag ) ? featureFlag : [ featureFlag ],
                      accessCount = [];

                // iterate
                keys.map( keyPath => {

                    // get the value from featureflags based a path from the string
                    const flagPathValue = _.get( featureFlags, keyPath.split( '.' ) );

                    // See if the pass value matches (usually needs just a true "true")
                    accessCount.push( flagPathValue === featureFlagPassValue );

                } );

                // Do we have access, given any values not null/false and/or all criteria is met?
                hasAccess = inclusiveCriteria
                            ? ( ( _.compact( accessCount ).length ) == keys.length )
                            : ( ( _.compact( accessCount ).length ) > 0 )
            }
        }

        return hasAccess;
    };

    let { children, replaceWith } = props,
        { inclusiveCriteria }     = props,
        { redirect, onDeny }      = props,
        hasAccess                 = hasAccessFromGivenPolicies(),
        hasRole                   = hasAccessFromGivenRoles(),
        canBypass                 = hasAccesFromBypassCriteria(),
        hasFeatureFlagAccess      = hasAccessFromFeatureFlag(),
        returnComponent           = null;

    /*console.log( `[ROLE ACCESS CHECK]:
        \n- Context: ${ props.roleContext }
        \n- Policies: ${ props.policies }
        \n- Roles: ${ props.roles }` );*/

    if( inclusiveCriteria && hasAccess && hasRole ){
        // console.log( 'User has access to all policies and is a role of inclusive criteria' );
        returnComponent = children;
    } else if( !inclusiveCriteria && hasAccess ){
        // console.log( 'User has access to one or more policies' );
        returnComponent = children;
    } else if( !inclusiveCriteria && hasRole ){
        // console.log( 'User has access to one or more roles' );
        returnComponent = children;
    } else if( canBypass ){
        // console.log( 'User allowed to bypass access control' );
        returnComponent = children;
    } else if( hasFeatureFlagAccess ){
        // console.log( 'User has feature flag access' );
        returnComponent = children;
    } else if( replaceWith ){
        // console.log( 'User access denied, replacing.' );
        returnComponent = replaceWith;
        onDeny();
    } else if( _.isString( redirect ) ){
        // console.log( 'User access denied, redirecting...' );
        returnComponent = <Route render={ props => <Redirect to={ { pathname: redirect, state: { from: location } } }/> }/>;
        onDeny();
    } else {
        // console.log( 'User access denied.' );
        onDeny();
    }

    return <React.Fragment>{ returnComponent }</React.Fragment>;

};

RoleAccessControl.defaultProps = defaultProps;

const SystemAccessControl: FC<RoleAccessControlProps> = ( props ) => {
    return <RoleAccessControl roleContext={ RoleAccessService.ROLE_CONTEXTS.SYSTEM } { ...props } />
};

const SystemAdminsOnly: FC<RoleAccessControlProps> = ( props ) => {
    return <SystemAccessControl roles={ [
        SYSTEM_ROLES.CORVALADMIN,
        SYSTEM_ROLES.ACCOUNTSADMIN,
        SYSTEM_ROLES.DECISIONENGINEADMIN,
    ] } inclusiveRoles={ false } { ...props } />
};

const SystemAccountAdminsOnly: FC<RoleAccessControlProps> = ( props ) => {
    return <SystemAccessControl roles={ [
        SYSTEM_ROLES.CORVALADMIN,
        SYSTEM_ROLES.ACCOUNTSADMIN,
    ] } inclusiveRoles={ false } { ...props } />
};

const SystemDEMAdminsOnly: FC<RoleAccessControlProps> = ( props ) => {
    return <SystemAccessControl roles={ [
        SYSTEM_ROLES.CORVALADMIN,
        SYSTEM_ROLES.DECISIONENGINEADMIN,
    ] } inclusiveRoles={ false } { ...props } />
};

const SystemMembersOnly: FC<RoleAccessControlProps> = ( props ) => {
    return <SystemAccessControl roles={ [
        SYSTEM_ROLES.MEMBER
    ] } inclusiveRoles={ false } { ...props } />
};

const OrganizationAccessControl: FC<RoleAccessControlProps> = ( props ) => {
    return <RoleAccessControl roleContext={ RoleAccessService.ROLE_CONTEXTS.ORGANIZATION } { ...props } />
};

const OrganizationAdminsOnly: FC<RoleAccessControlProps> = ( props ) => {
    return <OrganizationAccessControl roles={ [ ORGANIZATIONAL_ROLES.ADMIN ] } { ...props } />
};

const ProjectAccessControl: FC<RoleAccessControlProps> = ( props ) => {
    return <RoleAccessControl roleContext={ RoleAccessService.ROLE_CONTEXTS.PROJECT } { ...props } />
};

const ProjectAdminsOnly: FC<RoleAccessControlProps> = ( props ) => {
    return <ProjectAccessControl roles={ [ PROJECT_ROLES.ADMIN ] } { ...props } />
};

export {
    RoleAccessControl,
    SystemAccessControl,
    SystemAccountAdminsOnly,
    SystemDEMAdminsOnly,
    SystemMembersOnly,
    OrganizationAccessControl,
    ProjectAccessControl,
    SystemAdminsOnly,
    OrganizationAdminsOnly,
    ProjectAdminsOnly,
};
